import { BigPictureClient, Configuration, Event, Service } from '@vimeo/bigpicture-client';
import { Viewer } from '@vimeo/viewer';

import type { SessionReplay } from './amplitude-session-replay';

// Prevent other packages from overwriting the BigPictureClient config we set
const bigPictureClientConfig = BigPictureClient.configure;
BigPictureClient.configure = (...args: any[]) => void 0;

const getEnvironment = () => {
  const ciRegex = /\.ci\.vimeows.com$/;
  const environment = ciRegex.test(window.location.host)
    ? 'staging'
    : process.env.NODE_ENV === 'production'
      ? 'production'
      : 'development';

  return environment;
};

const isOrgRoute = () => {
  return window.location.pathname.includes('/organization/');
};

/**
 * Polls for the presence of the Singular SDK for up to 2 seconds before resolving.
 */
const waitForSingular = () =>
  new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 10;
    const intervalTime = 200;

    const interval = setInterval(() => {
      attempts += 1;

      if (window.singularSdk?._singularInstance?._singularState) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(true);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(true);
      }
    }, intervalTime);

    const timeout = setTimeout(() => {
      resolve(true);
      clearInterval(interval);
    }, maxAttempts * intervalTime);
  });

/**
 * Bootstrap the bigpicture client.
 */
export async function bootstrapBigPicture(viewer: Viewer) {
  await waitForSingular();

  let sessionReplay: SessionReplay | undefined;
  if (viewer.user) {
    // Dynamically import the session replay module for logged-in users
    sessionReplay = (await import('./amplitude-session-replay')).default;
    await sessionReplay.init({
      sessionId: Date.now(),
      deviceId: viewer.vuid,
      // Opt out of session replay for countries that qualify for GDPR
      optOut: viewer.gdprQualifies ?? false,
    });
  }

  const { locale, vuid, ribbonModifier, user } = viewer;

  const flags: string[] = [];
  if (user?.capabilities?.hasEnterpriseLihp) {
    flags.push('has_video_library');
  }

  const bpFrontendContext = new Event('vimeo.web_global', 9, {
    session_replay_id: sessionReplay?.getSessionReplayId() ?? null,
    workspace_id: viewer.teamUser ? `${viewer.teamUser.ownerId}` : null,
    organization_id: viewer.user?.organizationId ?? null,
    owner_id: isOrgRoute()
      ? (viewer.user?.organizationId ?? null)
      : viewer.teamUser?.ownerId != null
        ? String(viewer.teamUser.ownerId)
        : null,
    user: {
      subscription_type: user?.account ?? null,
      is_mod: ribbonModifier === 'mod' || user?.badge.type === 'staff',
      session_id: null,
      teams: null,
      user_id: user?.id ?? null,
      vuid: vuid,
      flags,
      is_free_trial: user?.isFreeTrial ?? false,
      singular_session_id:
        window.singularSdk?._singularInstance?._singularState?.getSessionId() || null,
    },
    application: {
      application: 'vimeo',
      vimeo_language: locale,
      product: 'app_shell',
      build_environment: getEnvironment(),
      version: {
        full: '1.0.0',
        major: '1',
        minor: '0',
        patch: '0',
      },
    },
    platform: {
      device_language: window.navigator?.language ?? '',
      screen: {
        dpi: screen.pixelDepth,
        height: screen.height,
        width: screen.width,
        size: `${screen.width} x ${screen.height}`,
      },
      network: null,
      platform: 'web',
      singular_device_id:
        window.singularSdk?._singularInstance?._singularState?.getSingularDeviceId() || null,
    },
  });

  bigPictureClientConfig(
    new Configuration(
      process.env.NODE_ENV === 'production' && !(getEnvironment() === 'staging')
        ? window.location.hostname === 'videoji.cn'
          ? Service.FRESNEL_PROD_CN
          : Service.FRESNEL_PROD
        : Service.FRESNEL_DEV,
      bpFrontendContext,
    ),
  );
}
